// I capitoli della pagina delle regole dei tornei, in tabella.
//
// Niente React, niente DOM: gira uguale nel browser e nel prerender Node, come
// `deriveRules.ts` e `deriveCaptureRules.ts`.
//
// A differenza di quei due qui NON si deriva niente da uno `VariantSpec`: le
// regole dei tornei non dipendono dalla variante, sono le stesse per tutte.
// Quindi non c'e' una funzione `derive*`, c'e' una tabella — e il suo valore
// sta tutto nel TIPO che esporta.
//
// `TournamentRulesAnchor` e' l'ancora nel DOM, il frammento dell'URL e il tipo
// dell'argomento che i tornei passano per aprire il capitolo giusto. Prima
// questi id erano stringhe libere passate alla FAQ, e li sorvegliava un test a
// runtime; ora un'ancora scritta storta non compila.
//
// Due forme per lo stesso capitolo, ciascuna nella convenzione di casa sua:
// l'ANCORA e' kebab-case perche' finisce in un URL (`…/tornei#shared-places`),
// la CHIAVE i18n e' camelCase come tutto il resto di `rules.*`. La tabella qui
// sotto e' il posto dove le due forme si incontrano, ed e' esattamente per
// questo che esiste.

/** Le nove ancore, nell'ordine in cui si leggono. */
export type TournamentRulesAnchor =
  | 'format'
  | 'rounds'
  | 'entry'
  | 'absences'
  | 'opening-moves'
  | 'rated'
  | 'tiebreaks'
  | 'shared-places'
  | 'arena';

export interface TournamentRuleSection {
  /** Ancora nel DOM (`id=`) e nel frammento dell'URL. */
  id: TournamentRulesAnchor;
  /** `rules.tournaments.<capitolo>.title` — il titolo, affermativo. */
  titleKey: string;
  /** `rules.tournaments.<capitolo>.body` — ARRAY di paragrafi, non una stringa. */
  bodyKey: string;
  /**
   * `rules.tournaments.rounds.rounds` — la tabella degli scaglioni, l'unico
   * pezzo di questa pagina che non e' prosa. Ce l'ha solo il capitolo `rounds`.
   */
  rowsKey?: string;
  /**
   * Nessun capitolo ha (ancora) un diagramma: questa e' l'unica pagina del
   * sotto-albero delle regole fatta di solo testo. Se un giorno servira'
   * disegnare una tabella di Berger o un accoppiamento svizzero, il campo e'
   * qui che va, accanto agli altri, come fa `CaptureSection.exampleId`.
   */
  exampleId?: string;
}

const capitolo = (
  id: TournamentRulesAnchor,
  chiave: string,
  rows = false
): TournamentRuleSection => ({
  id,
  titleKey: `rules.tournaments.${chiave}.title`,
  bodyKey: `rules.tournaments.${chiave}.body`,
  ...(rows ? { rowsKey: `rules.tournaments.${chiave}.rounds` } : {}),
});

/**
 * L'ordine e' quello di lettura, e non e' casuale: prima che cos'e' un torneo
 * (`format`, `rounds`), poi come ci si entra (`entry`) e cosa succede se non ci
 * si presenta, poi come si gioca (`opening-moves`) e quanto conta (`rated`),
 * infine come si esce dalla classifica (`tiebreaks`, `shared-places`).
 * L'arena chiude perche' e' l'altra gara, e il primo capitolo la nomina
 * rimandando qui.
 *
 * `tiebreaks` e `shared-places` stanno vicini e si somigliano apposta: il primo
 * sono i criteri, il secondo e' cosa succede quando nemmeno quelli separano.
 */
export const TOURNAMENT_RULE_SECTIONS: readonly TournamentRuleSection[] = [
  capitolo('format', 'format'),
  capitolo('rounds', 'rounds', true),
  capitolo('entry', 'entry'),
  capitolo('absences', 'absences'),
  capitolo('opening-moves', 'openingMoves'),
  capitolo('rated', 'rated'),
  capitolo('tiebreaks', 'tiebreaks'),
  capitolo('shared-places', 'sharedPlaces'),
  capitolo('arena', 'arena'),
];

/**
 * Le ancore di quando queste nove regole erano voci della FAQ.
 *
 * Quelle URL — `…/{faq}#faq-tornei-turni` — sono gia' state mandate in chat e
 * indicizzate, e il frammento non arriva mai al server: nessun redirect di
 * nginx puo' vederle. Questa tabella e' l'unico modo di farle ancora atterrare
 * dove il testo e' andato a vivere.
 *
 * E' il prezzo, dichiarato, di avere ancore inglesi: finche' gli id erano
 * rimasti italiani il ponte era una RegExp e bastava. Va tenuta finche' quei
 * link circolano — cioe' a lungo, perche' un indirizzo mandato in chat non
 * scade.
 */
export const ANCORE_STORICHE: Readonly<Record<string, TournamentRulesAnchor>> = {
  'tornei-formato': 'format',
  'tornei-turni': 'rounds',
  'tornei-iscrizione': 'entry',
  'tornei-assenza': 'absences',
  'tornei-apertura': 'opening-moves',
  'tornei-valutati': 'rated',
  'tornei-spareggi': 'tiebreaks',
  'tornei-parita': 'shared-places',
  'tornei-arena': 'arena',
};

/**
 * Il capitolo chiesto da un'ancora qualunque: quella di oggi o quella storica.
 * Torna `null` per tutto il resto, cosi' un frammento sconosciuto non manda
 * nessuno da nessuna parte.
 */
export function ancoraCapitolo(valore: string): TournamentRulesAnchor | null {
  if (valore in ANCORE_STORICHE) return ANCORE_STORICHE[valore]!;
  const vive = TOURNAMENT_RULE_SECTIONS.map((s) => s.id) as string[];
  return vive.includes(valore) ? (valore as TournamentRulesAnchor) : null;
}
